// src/encounter/structured-note/risk-record-lines.ts
//
// PROD-3042 (F7, the 6 Oct live review; PROD-3030's pick-list line pattern) · frame v2 ONLY. The Risk
// block's Fall and Pain assessments as note lines, like every other item, in place of the DS renderer's
// Fall / Pain cards (a switch, a "Done" button and a "Select…" dropdown — L6, no legacy UI in frame v2).
//
// WHAT THE LINES ARE — exactly the parts the DS cards draw, found the same way (`fallAssessmentParts` /
// `painAssessmentParts`, by structure, never by linkId), so the lines can hold nothing the cards could not:
//   Fall · "Fall risk present" (yes/no) · Morse answers of an OLD record (read-only history, 7 Oct 2026 ruling) and
//          their total (read-only text)
//   Pain · "Pain present" (yes/no) · the pain scale / Wong-Baker score of an OLD record (read-only history) · the
//          detail items (a pick, a text line, or the DS field for a date)
// As on the cards, the questions, the scale, the score and the details show once "present" is Yes. A line
// that already holds an answer always shows, so a recorded answer is never hidden.
//
// THE WRITE DOES NOT CHANGE. Every line writes an ordinary answer to the same linkId the card wrote, into
// the one live response, and the page's Risk bridge (`useRiskBridge.mirror`, risk-qr-bridge.ts) forwards it
// to EncounterMetaData Fall / Pain on change — as it did for the card. The two card side writes are kept:
// a Morse answer rewrites the total beside it, and an untouched switch with an authored `initial` is
// written with the first real answer (`withPresent`). There is no "Done": the answer saves on change.
import {
  bandForScore,
  fallAssessmentParts,
  fallTotal,
  isBulkExempt,
  painAssessmentParts,
  qrInitialBoolean,
  qrItemEnabled,
  type QItem,
} from '@scripsteam/scrips-signal-ds';
import { riskBridgeScope } from '../risk/lib/risk-qr-bridge';
import { isAnswered, pickOptions, prunedGroup, type AnswerMap, type AnswerUpdate, type NarrativeRow } from './note-adapter';

/** One Fall / Pain line: a note row the note already draws, the read-only Morse total, a date (K7, PROD-2947), or a
 *  licensed answer from an old record shown as read-only history (`history`; the 7 Oct 2026 ruling). */
export type RiskRecordRow =
  | Exclude<NarrativeRow, { kind: 'chips' }>
  | { kind: 'total'; item: QItem; text: string }
  | { kind: 'date'; item: QItem }
  | { kind: 'history'; item: QItem; text: string }
  /** PROD-3128: the STEADI follow-up's own heading (its source and population, "adults 65 and older"), drawn above it. */
  | { kind: 'heading'; item: QItem; text: string };

export interface RiskRecordGroup {
  kind: 'fall' | 'pain';
  /** The bridged group (`fall-assessment` / `wong-baker-pain`), as authored. */
  group: QItem;
  rows: RiskRecordRow[];
}

/** The card's switch rule: the answer, else the authored `initial`, else — with no switch — on. */
export function presentOn(present: QItem | undefined, values: AnswerMap): boolean {
  if (!present) return true;
  const v = values[present.linkId];
  return v === undefined ? qrInitialBoolean(present) === true : v === true;
}

/** The DS band words (risk-block-render.tsx `BAND_LABELS`), so the line reads as the card did. */
const BAND_WORDS: Record<string, string> = { low: 'Low risk', moderate: 'Moderate risk', high: 'High risk' };
const bandWords = (code: string): string => BAND_WORDS[code] ?? code.charAt(0).toUpperCase() + code.slice(1);

/** A leaf as a note line: the note's own row kinds (a date included, K7); anything with no line yet goes to the DS field. */
function leafRow(owner: QItem, item: QItem): RiskRecordRow {
  if (item.type === 'date' || item.type === 'dateTime') return { kind: 'date', item };
  if ((item.type === 'choice' || item.type === 'open-choice') && (item.answerOption?.length ?? 0) > 0) {
    return { kind: 'pick', item, options: pickOptions(item), multiple: !!item.repeats };
  }
  if (item.type === 'boolean') return { kind: 'yes-no', item };
  if (item.type === 'string' || item.type === 'text') return { kind: 'line', item };
  if (item.type === 'integer' || item.type === 'decimal') return { kind: 'number', item };
  return { kind: 'fields', group: prunedGroup(owner, [item]) };
}

/**
 * Founder ruling, 7 Oct 2026 (licensing; encounter authority "RULED by Samer, 7 Oct 2026"): CDC STEADI and NRS 0–10
 * replace Morse and Wong-Baker everywhere. Here (frame v2), a Morse question, the Wong-Baker score and the pain scale are
 * READ-ONLY HISTORY: drawn only when an old record holds an answer, as its text, with no control; never asked on a new
 * record, never written. The Morse total shows only beside such answers, computed for display, never saved.
 */
function history(item: QItem, values: AnswerMap): RiskRecordRow | undefined {
  const v = values[item.linkId];
  if (!isAnswered(v)) return undefined;
  const labels = (Array.isArray(v) ? v : [v]).map((x) => pickOptions(item).find((o) => o.value === x)?.label ?? String(x));
  return { kind: 'history', item, text: labels.join(', ') };
}

/** An item the template's own enableWhen disables draws no line (the card's `ctx.isEnabled`). */
const enabled = (item: QItem | undefined, values: AnswerMap): item is QItem => !!item && qrItemEnabled(item, values);

function fallRows(group: QItem, values: AnswerMap, steadi?: QItem): RiskRecordRow[] {
  const { present: authoredPresent, morse, questions: all, total, bands } = fallAssessmentParts(group);
  const present = enabled(authoredPresent, values) ? authoredPresent : undefined;
  const questions = morse && !enabled(morse, values) ? [] : all.filter((q) => enabled(q, values));
  const rows: RiskRecordRow[] = [];
  if (present) rows.push({ kind: 'yes-no', item: present });
  // PROD-3128 (Andrew, answer (b)): the CDC STEADI follow-up a template without STEADI is given (steadi-follow-up.ts),
  // under "Fall risk present" as Pain's details sit under "Pain present": shown once present is Yes, and a line that
  // already holds an answer always shows. Its lines are the note's own (a pick, a text line), never Morse.
  if (present && steadi) {
    const on = presentOn(present, values);
    const shown = (steadi.item ?? []).filter((q) => on || isAnswered(values[q.linkId]));
    // The heading says what STEADI is and for whom ("adults 65 and older"), as on the adult template.
    if (shown.length > 0 && steadi.text) rows.push({ kind: 'heading', item: steadi, text: steadi.text });
    for (const q of shown) rows.push(leafRow(steadi, q));
  }
  // Morse: history only (an old record's answers), never asked.
  const answered = questions.filter((q) => isAnswered(values[q.linkId]));
  for (const q of answered) {
    const row = history(q, values);
    if (row) rows.push(row);
  }
  // The DS total is undefined while no Morse question holds an answer, so it shows only beside an old record's answers.
  const sum = fallTotal(questions, values);
  if (total && enabled(total, values) && sum !== undefined) {
    const band = bands.length > 0 ? bandForScore(sum, bands) : undefined;
    rows.push({ kind: 'total', item: total, text: band ? `${sum} · ${bandWords(band.code)}` : String(sum) });
  }
  return rows;
}

/** `signalNurseEncounter` (4 Nov rulings): the nurse's pain is Present + NRS 0-10 + a one-line location. Of the details only those
 *  are asked; a detail that already holds an answer (an earlier record) still shows, so no recorded fact is hidden. */
const NURSE_PAIN_DETAIL = /\blocation\b|\bnrs\b|0\s*[-–]\s*10/i;
export const isNursePainDetail = (item: QItem): boolean => NURSE_PAIN_DETAIL.test(item.text ?? '');

function painRows(group: QItem, values: AnswerMap, nursePain = false): RiskRecordRow[] {
  const parts = painAssessmentParts(group);
  const present = enabled(parts.present, values) ? parts.present : undefined;
  const on = presentOn(present, values);
  const shows = (item: QItem) => (on && (!nursePain || isNursePainDetail(item))) || isAnswered(values[item.linkId]);
  const rows: RiskRecordRow[] = [];
  if (present) rows.push({ kind: 'yes-no', item: present });
  // The pain scale and the Wong-Baker score: history only (an old record's answers), never asked.
  for (const licensed of [parts.scale, parts.wongBaker]) {
    if (!enabled(licensed, values)) continue;
    const row = history(licensed, values);
    if (row) rows.push(row);
  }
  const details = enabled(parts.details, values) ? parts.details : undefined;
  const detailRows: RiskRecordRow[] = [];
  for (const d of details?.item ?? []) {
    if (d.type === 'group' || !qrItemEnabled(d, values) || !shows(d)) continue;
    detailRows.push(leafRow(details as QItem, d));
  }
  rows.push(...oneLinePerFact(detailRows));
  return rows;
}

const foldLabel = (s: string | undefined) => (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * G7 (one place per fact; PROD-2947, the 6 Oct contract): a text line and a pick line the template authored under the
 * SAME label (the live Pain template's "Location": a text item and a choice item) are one fact. The pick keeps the
 * line, at the place the first of the two was authored; the text is drawn beside it as its K6 detail. Only one text
 * pairs with one pick; anything else is left as authored.
 */
export function oneLinePerFact(rows: readonly RiskRecordRow[]): RiskRecordRow[] {
  const out: RiskRecordRow[] = [...rows];
  for (let i = 0; i < out.length; i += 1) {
    const text = out[i];
    if (text.kind !== 'line') continue;
    const label = foldLabel(text.item.text);
    if (!label) continue;
    const j = out.findIndex((r, k) => k !== i && r.kind === 'pick' && !r.detail && foldLabel(r.item.text) === label);
    if (j < 0) continue;
    const pick = out[j] as Extract<RiskRecordRow, { kind: 'pick' }>;
    const merged: RiskRecordRow = { ...pick, detail: text.item };
    const at = Math.min(i, j);
    const next = out.filter((_, k) => k !== i && k !== j);
    next.splice(at, 0, merged);
    out.splice(0, out.length, ...next);
    i = at;
  }
  return out;
}

/** The Fall and Pain groups under `items` (the Risk record part), each with its note lines. `steadi`: the STEADI
 *  follow-up the page offers under "Fall risk present" (PROD-3128; absent → none). */
export function riskRecordGroups(items: readonly QItem[], values: AnswerMap, steadi?: QItem, nursePain = false): RiskRecordGroup[] {
  const { fall, pain } = riskBridgeScope(items);
  const out: RiskRecordGroup[] = [];
  // A group the template disables (its own enableWhen) draws nothing, as the card did not draw.
  if (fall && enabled(fall, values)) out.push({ kind: 'fall', group: fall, rows: fallRows(fall, values, steadi) });
  if (pain && enabled(pain, values)) out.push({ kind: 'pain', group: pain, rows: painRows(pain, values, nursePain) });
  // Authored order: whichever the template puts first draws first.
  const order = (g: RiskRecordGroup) => indexOf(items, g.group.linkId);
  return out.sort((a, b) => order(a) - order(b));
}

/** PROD-3128 (Codex P2): whether the STEADI follow-up draws any line under these answers. Its managed line in the Fall
 *  record stands in for it only when it does not (a template that disables the Fall group or its question). */
export function steadiFollowUpDrawn(items: readonly QItem[], values: AnswerMap, steadi: QItem | undefined): boolean {
  if (!steadi) return false;
  const ids = new Set((steadi.item ?? []).map((i) => i.linkId));
  return riskRecordGroups(items, values, steadi).some((g) => g.kind === 'fall' && g.rows.some((r) => 'item' in r && ids.has(r.item.linkId)));
}

/** PROD-3142 (D-78): every linkId under the Fall / Pain group(s) named — what a frozen list's bulk actions must not touch. */
export function riskGroupLinkIds(items: readonly QItem[], kinds: { fall?: boolean; pain?: boolean } | undefined): ReadonlySet<string> {
  const out = new Set<string>();
  if (!kinds?.fall && !kinds?.pain) return out;
  const { fall, pain } = riskBridgeScope(items);
  const walk = (it: QItem | undefined) => {
    if (!it) return;
    out.add(it.linkId);
    (it.item ?? []).forEach(walk);
  };
  if (kinds.fall) walk(fall);
  if (kinds.pain) walk(pain);
  return out;
}

function indexOf(items: readonly QItem[], linkId: string): number {
  let i = 0;
  let at = -1;
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (at >= 0) return;
      if (it.linkId === linkId) at = i;
      i += 1;
      walk(it.item ?? []);
    }
  };
  walk(items);
  return at < 0 ? Number.MAX_SAFE_INTEGER : at;
}

/**
 * The writes one line change makes, with the card's two side writes:
 *  · a Morse answer rewrites the total from the answers as they will be (`fallTotal`; no answer → none);
 *  · any answer other than the switch, while the switch is untouched and has an authored `initial`,
 *    writes that `initial` first (the card's `withPresent`), so the saved assessment says "present".
 */
/** The card's `withPresent`: the untouched switch's authored `initial`, to be written beside a first real answer
 *  (none when the switch is answered, has no `initial`, or is bulk-exempt). */
export function presentPrelude(group: RiskRecordGroup, values: AnswerMap): AnswerUpdate[] {
  const present = group.kind === 'fall' ? fallAssessmentParts(group.group).present : painAssessmentParts(group.group).present;
  const initial = present ? qrInitialBoolean(present) : undefined;
  // A switch the template disables is never written (Codex review, round 2).
  return present && enabled(present, values) && values[present.linkId] === undefined && initial !== undefined && !isBulkExempt(present)
    ? [{ linkId: present.linkId, value: initial }]
    : [];
}

export function riskRecordWrites(group: RiskRecordGroup, values: AnswerMap, updates: readonly AnswerUpdate[]): AnswerUpdate[] {
  if (updates.length === 0) return [];
  const parts = group.kind === 'fall' ? fallAssessmentParts(group.group) : undefined;
  const present = parts ? parts.present : painAssessmentParts(group.group).present;
  const out: AnswerUpdate[] = [];
  if (updates.some((u) => u.linkId !== present?.linkId)) out.push(...presentPrelude(group, values));
  out.push(...updates);
  // 7 Oct 2026 ruling: the Morse total is never computed into a save (the card used to rewrite it beside each answer);
  // it is drawn from an old record's answers only. A Morse answer is never written here at all (history rows).
  return out;
}
